import { useTheme } from '@emotion/react';
import { Down } from '@icon-park/react';
import { head, last, noop } from 'lodash';
import { Fragment, type HTMLProps, memo, useCallback, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { globalMessages } from '@didaskill/ui/intl';
import { useBoolState } from '@didaskill/ui/utils';

import { Typography } from '../../general/typography/Typography';
import SeeMoreDropDown from './SeeMoreDropdown';
import { Separator } from './Separator';
import { Tab } from './Tab';
import { TabGroup } from './TabGroup';
import * as styles from './Tabs.styles';
import type { TabKey, TabShape } from './types';
import { useFitTabsToContainer } from './useFitTabsToContainer';

export type TabsProps<TTabKey extends TabKey = TabKey> = Omit<
  HTMLProps<HTMLDivElement>,
  'ref' | 'onChange' | 'value'
> & {
  /** Currently active tab key. */
  value: TTabKey | null;
  /** On change handler, called with the new active tab key. */
  onChange: (key: TTabKey) => void;
  /** On drag end change handler, called with the new tabs order. */
  onChangeOrder?: (newOrder: TabShape<TTabKey>[], index: number) => void;
  /** Should display tabs vertically. */
  vertical?: boolean;
  /** Tabs or tabs groups. */
  tabs: TabShape<TTabKey>[] | TabShape<TTabKey>[][];
  /** Should tabs be draggable. */
  isDraggable?: boolean;
  /** Should drag be disabled. */
  isDragDisabled?: boolean;
  /**
   * When true, measures the container and tab widths to decide how many tabs fit
   * before grouping the rest into the "See more" dropdown.
   * If `maxNbTabsBeforeGrouping` is also set, it acts as an upper cap on the fitted count.
   */
  fitToContainer?: boolean;
  /**
   * The `maxNbTabsBeforeGrouping` prop defines the maximum number of tabs to display
   * before grouping the remaining tabs into a dropdown (for cases when there are too many tabs to show).
   *
   * This value must be manually managed based on the screen width to ensure proper responsive behavior.
   * For example, on smaller screens, you may want to display fewer tabs and move the rest into the dropdown.
   * This means you should dynamically adjust `maxNbTabsBeforeGrouping` according to the viewport size to provide
   * an optimal user experience across different devices.
   *
   * When combined with `fitToContainer`, this value caps the number of visible tabs
   * after the available width has been measured.
   */
  maxNbTabsBeforeGrouping?: number | null;
};

const DEFAULT_TABS = [];

/**
 * Resolves the effective max visible tabs from an optional breakpoint cap and a fitted count.
 * @param maxNbTabsBeforeGrouping Optional upper cap from the caller (e.g. breakpoint).
 * @param fittedCount Measured number of tabs that fit in the container.
 * @param tabsCount Total number of flat tabs.
 * @returns The effective max before grouping, or `null` when all tabs should stay visible.
 */
function resolveMaxNbTabsBeforeGrouping(
  maxNbTabsBeforeGrouping: number | null | undefined,
  fittedCount: number | null,
  tabsCount: number,
): number | null {
  let effectiveMax = maxNbTabsBeforeGrouping ?? null;

  if (fittedCount != null) {
    effectiveMax = effectiveMax == null ? fittedCount : Math.min(effectiveMax, fittedCount);
  }

  if (effectiveMax == null || effectiveMax >= tabsCount) {
    return null;
  }

  return effectiveMax;
}

export const TabsBase = <TTabKey extends TabKey = TabKey>({
  value = null,
  onChange = noop,
  onChangeOrder = noop,
  vertical = false,
  tabs = DEFAULT_TABS,
  isDraggable = false,
  isDragDisabled = false,
  fitToContainer = false,
  maxNbTabsBeforeGrouping = null,
  ...props
}: TabsProps<TTabKey>) => {
  const theme = useTheme();

  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const seeMoreMeasureRef = useRef<HTMLDivElement>(null);

  const [isVisible, , , onToggle] = useBoolState();

  const isFlatTabs = tabs.length > 0 && !Array.isArray(tabs[0]);
  const flatTabs = isFlatTabs ? (tabs as TabShape<TTabKey>[]) : null;
  const shouldFitToContainer = fitToContainer && !vertical && !!flatTabs;

  const fittedCount = useFitTabsToContainer({
    containerRef,
    enabled: shouldFitToContainer,
    measureRef,
    seeMoreMeasureRef,
  });

  const effectiveMaxNbTabsBeforeGrouping = resolveMaxNbTabsBeforeGrouping(
    maxNbTabsBeforeGrouping,
    shouldFitToContainer ? fittedCount : null,
    flatTabs?.length ?? 0,
  );

  const sortTabs = useCallback((group: TabShape<TTabKey>[], index: number, key: string) => {
    const newIndexForOldItem = group.findIndex((tab) => tab.key === key);

    if (newIndexForOldItem < index || newIndexForOldItem === -1) {
      return group;
    }

    // uses destructuring to exchange the positions of two elements in an array
    [group[index], group[newIndexForOldItem]] = [group[newIndexForOldItem], group[index]];

    return group;
  }, []);

  const tabsGroups = useMemo(
    () =>
      (tabs.length > 0 && !Array.isArray(tabs[0])
        ? [
            effectiveMaxNbTabsBeforeGrouping && effectiveMaxNbTabsBeforeGrouping < tabs.length
              ? sortTabs(
                  tabs as TabShape<TTabKey>[],
                  effectiveMaxNbTabsBeforeGrouping - 1,
                  value.toString(),
                )
              : (tabs as TabShape<TTabKey>[]),
          ]
        : (tabs as TabShape<TTabKey>[][])
      ).filter((tabGroup) => !!tabGroup.length),
    [effectiveMaxNbTabsBeforeGrouping, sortTabs, tabs, value],
  );

  const dropdownItems = useMemo(
    () =>
      effectiveMaxNbTabsBeforeGrouping && tabsGroups.length
        ? tabsGroups[0].slice(effectiveMaxNbTabsBeforeGrouping)
        : null,
    [effectiveMaxNbTabsBeforeGrouping, tabsGroups],
  );

  const onChangeItemDropdown = useCallback(
    (key: string) => {
      onToggle();
      onChange(key as TTabKey);
    },
    [onChange, onToggle],
  );

  return (
    <div
      {...props}
      ref={containerRef}
      css={
        vertical
          ? styles.verticalContainer
          : styles.container(theme, !!effectiveMaxNbTabsBeforeGrouping)
      }
    >
      {shouldFitToContainer && flatTabs && (
        <>
          <div ref={measureRef} css={styles.measureRow} aria-hidden>
            {flatTabs.map((tab) => (
              <Tab<TTabKey> key={tab.key} tab={tab} isActive={tab.key === value} vertical={false} />
            ))}
          </div>
          <div ref={seeMoreMeasureRef} css={[styles.measureRow, styles.seeMoreMeasure]} aria-hidden>
            <Typography variant={Typography.VARIANTS.BODY_SMALL_BOLD}>
              <button type="button" css={styles.seeMore} tabIndex={-1}>
                <FormattedMessage {...globalMessages.SEE_MORE} />
                <Down strokeWidth={6} />
              </button>
            </Typography>
          </div>
        </>
      )}

      {tabsGroups.map((groupTabs, index) => (
        <Fragment key={groupTabs[0].key}>
          <TabGroup<TTabKey>
            tabs={groupTabs.slice(0, effectiveMaxNbTabsBeforeGrouping ?? groupTabs.length)}
            index={index}
            tabGroupsCount={tabsGroups.length}
            value={value}
            onChange={onChange}
            onChangeOrder={onChangeOrder}
            isDraggable={isDraggable}
            isDragDisabled={isDragDisabled}
            vertical={vertical}
            maxNbTabsBeforeGrouping={effectiveMaxNbTabsBeforeGrouping}
          />

          {!vertical && index < tabsGroups.length - 1 && (
            <Separator
              isLeftActive={last(groupTabs).key === value}
              isRightActive={head(tabsGroups[index + 1]).key === value}
            />
          )}
        </Fragment>
      ))}
      {!!dropdownItems?.length && (
        <SeeMoreDropDown
          isVisible={isVisible}
          dropdownItems={dropdownItems}
          onChangeItemDropdown={onChangeItemDropdown}
          onToggle={onToggle}
        />
      )}
    </div>
  );
};

TabsBase.displayName = 'Tabs';

export const Tabs = memo(TabsBase) as unknown as typeof TabsBase;
